<template>
  <LayoutOverlay v-if="isOpen" @closeOverlay="closeModal">
    <div class="modal-container">
      <header class="header">
        <div class="title">
          <component :is="icon" v-if="icon" class="icon" />
          <TextBase
            v-if="title"
            :text="title"
            type="h4"
            weight="bold"
            overflow-tooltip
            overflow="ellipsis"
          />
        </div>
        <div v-if="!hideControls" class="controls">
          <slot name="controls" />
          <ButtonText
            class="controls-close__button"
            :iconLeft="iconXSmall"
            size="extra-small-icon"
            @onClicked="closeModal"
          ></ButtonText>
        </div>
      </header>
      <div v-if="!hideDivider" class="divider"></div>
      <main class="content">
        <slot name="content" />
      </main>
      <footer class="footer">
        <slot name="footer" />
      </footer>
    </div>
  </LayoutOverlay>
</template>

<script setup lang="ts">
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import TextBase from '@/design-system/atoms/TextBase.vue'
import LayoutOverlay from '@/design-system/templates/LayoutOverlay.vue'
import { onMounted, onUnmounted, ref, toRefs, watch } from 'vue'
import iconXSmall from '@/design-system/assets/icons/Icon-X-Small.svg'

type Props = {
  title: string
  open?: boolean
  headerPadding?: string
  contentPadding?: string
  footerPadding?: string
  icon?: string
  hideDivider?: boolean
  hideControls?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  open: false,
  icon: undefined,
  hideDivider: false,
  hideControls: false,
  headerPadding: '24px 24px 0',
  contentPadding: '16px 24px',
  footerPadding: '0 24px 24px'
})

const { title } = toRefs(props)

const isOpen = ref(props.open)

const emit = defineEmits<(e: 'close') => void>()

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})

const handleKeydown = (event: KeyboardEvent) => {
  if (!isOpen.value) return
  if (event.key === 'Escape') {
    closeModal()
  }
}

watch(
  () => props.open,
  (newValue) => {
    isOpen.value = newValue
  },
  { immediate: true }
)

const closeModal = () => {
  isOpen.value = false
  emit('close')
}
</script>

<style scoped lang="scss">
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';
.modal-container {
  background-color: variables.$color-pure-white;
  border-radius: variables.$border-radius-small;
  display: flex;
  flex-direction: column;
  min-width: 500px;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  height: fit-content;
  margin: 32px auto auto;
  overflow: hidden;
  max-width: calc(100vw - (32px + 32px));
  max-height: calc(100vh - (64px + 64px));
  width: max-content;

  .header {
    display: grid;
    grid-template-columns: 1fr auto;
    padding: v-bind(headerPadding);
    .controls {
      display: flex;
      padding: 8px 0px;
      gap: 8px;
      align-items: center;
      justify-content: flex-end;
      &-close__button {
        width: 24px;
      }
    }
  }

  .divider {
    background-color: variables.$color-greyscale-line-grey;
    height: 1px;
    margin: 16px 24px 0;
  }

  .content {
    margin-bottom: auto;
    padding: v-bind(contentPadding);
    overflow-y: auto;
    @include mixins.scrollbar-vertical;
    @include mixins.scrollbar-horizontal;
    height: 100%;
  }
  .footer {
    padding: v-bind(footerPadding);
  }
  .loading-spinner {
    margin: auto;
  }
  .title {
    display: flex;
    align-items: center;
    padding-right: 16px;
    gap: 8px;
    overflow: hidden;
  }
}
.icon {
  color: black;
}
</style>
